import type { ReactElement, ReactNode } from "react";

/**
 * Los iconos de la app, en SVG en línea.
 *
 * No hay librería de iconos y no se añade: son una veintena, se dibujan con el
 * mismo trazo y así pesan lo que pesan. Todos comparten la misma rejilla
 * (`viewBox` 24), trazo en `currentColor` —el color lo pone quien lo usa, con
 * `text-brand`, `text-zinc-500`…— y extremos redondeados. Son decorativos
 * (`aria-hidden`): el nombre lo lleva el control que los contiene.
 *
 * Sin `"use client"`: se pintan igual desde un Server Component (la portada de
 * ajustes) que desde uno de cliente.
 */

export type TamanoIcono = 20 | 22 | 24 | 32;

export interface IconoProps {
  /** Lado en píxeles. 20 en barras y filas, 24 en tarjetas, 32 en portadas. */
  tamano?: TamanoIcono;
  className?: string;
}

export type ComponenteIcono = (props: IconoProps) => ReactElement;

function Icono({
  tamano = 24,
  className,
  children,
}: IconoProps & { children: ReactNode }) {
  return (
    <svg
      width={tamano}
      height={tamano}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={["shrink-0", className ?? ""].join(" ").trim()}
    >
      {children}
    </svg>
  );
}

/* ── Ajustes del local ─────────────────────────────────────────────────── */

/** Local: la fachada con su toldo. */
export function IconoLocal(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5" />
      <path d="M3 5.5 4.5 4h15L21 5.5V8a2.5 2.5 0 0 1-4.5 1.5 2.5 2.5 0 0 1-4.5 0 2.5 2.5 0 0 1-4.5 0A2.5 2.5 0 0 1 3 8Z" />
      <path d="M9.5 20v-5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5" />
    </Icono>
  );
}

/** Sala: el comedor visto desde arriba, una mesa redonda y una cuadrada. */
export function IconoSala(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <circle cx="9" cy="9.25" r="2.25" />
      <rect x="13.25" y="13.25" width="4" height="4" rx="0.5" />
    </Icono>
  );
}

/** Horarios. */
export function IconoReloj(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icono>
  );
}

/** Calendario: una hoja de agenda con sus anillas y un día marcado. */
export function IconoCalendario(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
      <rect x="8" y="13" width="3" height="3" rx="0.5" />
    </Icono>
  );
}

/** Reglas del servicio: tres deslizadores. */
export function IconoReglas(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="18" r="2" />
    </Icono>
  );
}

/** Mensajes: el bocadillo de chat. */
export function IconoMensajes(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M20 11.5a7.5 7.5 0 0 1-11.1 6.6L4 19.5l1.4-4.4A7.5 7.5 0 1 1 20 11.5Z" />
      <path d="M8.5 10.5h7M8.5 13.5h4.5" />
    </Icono>
  );
}

/** Asistente de IA: dos destellos. */
export function IconoAsistente(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M10 4.5 11.4 8.6 15.5 10l-4.1 1.4L10 15.5l-1.4-4.1L4.5 10l4.1-1.4Z" />
      <path d="M17.5 14l.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8Z" />
    </Icono>
  );
}

/** Web y widget de reservas: el globo. */
export function IconoWeb(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.3 2.4 3.5 5.2 3.5 8.5s-1.2 6.1-3.5 8.5c-2.3-2.4-3.5-5.2-3.5-8.5S9.7 5.9 12 3.5Z" />
    </Icono>
  );
}

/** Equipo y usuarios: dos personas. */
export function IconoEquipo(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 6.4a3 3 0 0 1 0 5.2M17 14.4a5.5 5.5 0 0 1 3.5 5.1" />
    </Icono>
  );
}

/** Importar: una bandeja que recibe. */
export function IconoImportar(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M12 3.5v11" />
      <path d="m7.5 10 4.5 4.5 4.5-4.5" />
      <path d="M4 15.5V18a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.5" />
    </Icono>
  );
}

/* ── Acciones ──────────────────────────────────────────────────────────── */

/** Editar. También el «Dibujar aquí» del plano en el onboarding. */
export function IconoLapiz(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </Icono>
  );
}

/** Borrar. */
export function IconoPapelera(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M5.5 7l1 12a2 2 0 0 0 2 1.8h7a2 2 0 0 0 2-1.8l1-12" />
      <path d="M9 7V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V7" />
    </Icono>
  );
}

/** Ir a: flecha a la derecha. */
export function IconoFlecha(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M4.5 12h15" />
      <path d="m13.5 6 6 6-6 6" />
    </Icono>
  );
}

/** Volver: flecha a la izquierda. */
export function IconoAtras(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M19.5 12h-15" />
      <path d="m10.5 6-6 6 6 6" />
    </Icono>
  );
}

/** Cerrar un aviso o un diálogo: la aspa. */
export function IconoCerrar(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icono>
  );
}

/** El chevron de una tarjeta o fila que lleva a otra pantalla. */
export function IconoChevron(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icono>
  );
}

/**
 * El ángulo que dice si una fila está desplegada. Apunta hacia abajo; quien lo
 * usa lo gira con `rotate-180` cuando está abierta.
 */
export function IconoAngulo(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icono>
  );
}

/* ── Capas de `components/ui` (feature 028) ────────────────────────────── */

/** Buscar: la lupa del campo de una lista que se filtra escribiendo. */
export function IconoBuscar(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </Icono>
  );
}

/** Hecho o elegido: la marca ✓ de una opción, de un aviso o de un bloque. */
export function IconoCheck(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Icono>
  );
}

/** Algo que conviene mirar: el triángulo del aviso. */
export function IconoAviso(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M10.3 4.3 3 17a2 2 0 0 0 1.7 3h14.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4M12 16.8h.01" />
    </Icono>
  );
}

/** Algo que ha fallado: el círculo con la exclamación. Otra forma que el aviso, no solo otro color. */
export function IconoError(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v5M12 16.2h.01" />
    </Icono>
  );
}

/** Un dato que se cuenta, sin urgencia: la «i». */
export function IconoInfo(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.2M12 7.8h.01" />
    </Icono>
  );
}

/* ── Onboarding de sala (promovidos de `PantallasSala.tsx`) ───────────── */

/** «Hacer una foto de tu plano». */
export function IconoCamara(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.7l1.2-2h6.2l1.2 2h1.7A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5Z" />
      <circle cx="12" cy="12.5" r="3.4" />
    </Icono>
  );
}

export function IconoMovil(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 5.5h3M11 18.5h2" />
    </Icono>
  );
}

export function IconoOrdenador(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="2.5" y="4" width="19" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </Icono>
  );
}

/* ── Plataforma (superadmin) ───────────────────────────────────────────── */

/** Resumen: cuatro paneles. */
export function IconoPanel(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M4.75 5.75h6.5v5.5h-6.5Zm8 0h6.5v8h-6.5Zm0 9.5h6.5v3h-6.5Zm-8-2.5h6.5v5.5h-6.5Z" />
    </Icono>
  );
}

/** Soporte. */
export function IconoAyuda(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.2a2.6 2.6 0 0 1 5.1.8c0 1.7-1.9 2.2-2.4 2.9-.2.3-.2.6-.2 1.6" />
      <path d="M12 17.2h.01" />
    </Icono>
  );
}

/** Planes y facturación. */
export function IconoTarjeta(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 14.5h3" />
    </Icono>
  );
}

/** Plantillas de email. */
export function IconoSobre(props: IconoProps) {
  return (
    <Icono {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </Icono>
  );
}

/** Feature flags. */
export function IconoBandera(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M5 21V4" />
      <path d="M5 4.5h11l-2 3.75 2 3.75H5" />
    </Icono>
  );
}

/** Auditoría: la lista de lo que pasó. */
export function IconoHistorial(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
      <path d="M3.5 4.5V8H7" />
      <path d="M12 8v4l2.5 2" />
    </Icono>
  );
}

/** Salud del sistema: el pulso. */
export function IconoPulso(props: IconoProps) {
  return (
    <Icono {...props}>
      <path d="M3 12h4l2.5-6 5 12 2.5-6h4" />
    </Icono>
  );
}

/** Ajustes: el engranaje. */
export function IconoAjustes(props: IconoProps) {
  return (
    <Icono {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </Icono>
  );
}
